import React, { createContext, useContext, useState, useCallback } from "react";
import { useApp } from "./AppContext";

const ComponentEditorContext = createContext();

export const ComponentEditorProvider = ({ children, onPageChange }) => {
	const { pages, setPages, activePage, updatePage } = useApp();
	const [selectedComponent, setSelectedComponent] = useState(null);
	const [activeTab, setActiveTab] = useState(null);
	const selectComponent = (componentType, props, componentId) => {
		// This is used for legacy component selection
		// New approach uses handleSetSelectedComponent directly with component instance
		setSelectedComponent({
			componentType,
			props,
			componentId,
		});
	};

	const updateComponentProps = (propertyKey, value) => {
		if (!selectedComponent) return;

		// Call the component's updateProperty method if it exists
		if (selectedComponent.updateProperty && typeof selectedComponent.updateProperty === "function") {
			try {
				selectedComponent.updateProperty(propertyKey, value);
			} catch (error) {
				console.error("Error updating component property:", error);
			}
		} else {
			console.warn("Selected component does not have updateProperty method");
		}
	};

	const deleteComponent = useCallback(
		(componentId) => {
			setPages((currentPages) => {
				const updatedPages = currentPages.map((page) => {
					if (page.id !== activePage) return page;

					// Deep clone the page content
					const newContent = JSON.parse(JSON.stringify(page.content));

					// Remove the component from sections array
					if (newContent.sections) {
						newContent.sections = newContent.sections.filter((section) => section.id !== componentId);
					}

					// Update the page with new content
					return {
						...page,
						content: newContent,
						updatedAt: new Date().toISOString(),
					};
				});

				// Find the updated page
				const updatedPage = updatedPages.find((p) => p.id === activePage);
				if (updatedPage) {
					// Add to pending changes through the App context
					updatePage(activePage, updatedPage.content);
				}

				return updatedPages;
			});

			// Clear selected component if it was the one deleted
			if (selectedComponent?.componentId === componentId) {
				setSelectedComponent(null);
			}
		},
		[activePage, selectedComponent, updatePage]
	);

	const handleSetSelectedComponent = (component) => {
		if (component && typeof component === "object") {
			setSelectedComponent(component);
		} else {
			console.warn("Invalid component provided to setSelectedComponent");
		}
	};

	const value = {
		selectedComponent,
		setSelectedComponent: handleSetSelectedComponent,
		updateComponentProps,
		deleteComponent,
		selectComponent,
		onPageChange,
		setActiveTab,
		activeTab,
	};

	return <ComponentEditorContext.Provider value={value}>{children}</ComponentEditorContext.Provider>;
};

export const useComponentEditor = () => {
	const context = useContext(ComponentEditorContext);
	if (!context) {
		console.error("useComponentEditor must be used within a ComponentEditorProvider");
		throw new Error("useComponentEditor must be used within a ComponentEditorProvider");
	}
	return context;
};
